import { GoogleAiVoiceDraftV1, GoogleVoiceCatalogApiResponse, GoogleVoiceOption, LanguageOption } from '../types';

const LANGUAGE_LABEL_OVERRIDES: Record<string, string> = {
  'en-US': 'English (US)',
  'en-GB': 'English (UK)',
  'nb-NO': 'Norwegian',
};

const languageDisplay = typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames(['en'], { type: 'language' }) : null;

const regionDisplay = typeof Intl !== 'undefined' && typeof Intl.DisplayNames === 'function' ? new Intl.DisplayNames(['en'], { type: 'region' }) : null;

type GoogleVoiceApiCandidate = Partial<GoogleVoiceOption> & {
  id?: string;
  name?: string;
  languageCodes?: string[];
  ssmlGender?: string;
};

const normalizeVoiceGender = (voice: { gender?: unknown; ssmlGender?: unknown }) => String(voice.gender ?? voice.ssmlGender ?? '').trim();

export const dedupeVoices = (voices: GoogleVoiceOption[]): GoogleVoiceOption[] => {
  const indexByVoiceKey = new Map<string, number>();
  const deduped: GoogleVoiceOption[] = [];

  for (const voice of voices) {
    const voiceId = voice.voiceId.trim();
    const languageCode = voice.languageCode.trim();
    const gender = normalizeVoiceGender(voice);
    if (!voiceId || !languageCode) continue;

    const key = `${languageCode}::${voiceId}`;
    const existingIndex = indexByVoiceKey.get(key);
    if (existingIndex !== undefined) {
      // Keep previously selected voice entry, but backfill missing gender if a duplicate includes it.
      if (!deduped[existingIndex].gender && gender) {
        deduped[existingIndex] = { ...deduped[existingIndex], gender };
      }
      continue;
    }

    indexByVoiceKey.set(key, deduped.length);
    deduped.push({
      voiceId,
      displayName: (voice.displayName || voiceId).trim(),
      languageCode,
      gender,
    });
  }

  return deduped;
};

export const normalizeGoogleVoices = (raw: unknown, requestedLanguageCode = ''): GoogleVoiceOption[] => {
  const responseArray = Array.isArray(raw) ? raw : Array.isArray((raw as { voices?: unknown[] } | null)?.voices) ? (raw as { voices: unknown[] }).voices : [];

  return dedupeVoices(
    responseArray
      .map((entry): GoogleVoiceOption | null => {
        if (typeof entry === 'string') {
          const languageCode = requestedLanguageCode.trim();
          if (!languageCode) return null;
          return { voiceId: entry, displayName: entry, languageCode, gender: '' };
        }

        if (!entry || typeof entry !== 'object') return null;

        const candidate = entry as GoogleVoiceApiCandidate;
        const voiceId = (candidate.voiceId ?? candidate.id ?? candidate.name ?? '').trim();
        if (!voiceId) return null;

        const resolvedLanguage = candidate.languageCode ?? (Array.isArray(candidate.languageCodes) && candidate.languageCodes.length > 0 ? candidate.languageCodes[0] : requestedLanguageCode);
        const languageCode = (resolvedLanguage ?? '').trim();
        if (!languageCode) return null;

        return {
          voiceId,
          displayName: (candidate.displayName ?? voiceId).trim(),
          languageCode,
          gender: normalizeVoiceGender(candidate),
        };
      })
      .filter((voice): voice is GoogleVoiceOption => Boolean(voice)),
  );
};

export const normalizeGoogleLanguages = (raw: unknown, voicesByLanguage?: Record<string, GoogleVoiceOption[]>): LanguageOption[] => {
  const response = (raw as GoogleVoiceCatalogApiResponse | null) ?? {};
  const responseArray = Array.isArray(response?.languages) ? response.languages : [];

  if (responseArray.length < 1) {
    return voicesByLanguage ? toLanguageOptions(voicesByLanguage) : [];
  }

  const seen = new Set<string>();
  const normalized: LanguageOption[] = [];

  for (const entry of responseArray) {
    const code = String(entry?.languageCode ?? '').trim();
    if (!code || seen.has(code)) continue;
    seen.add(code);

    const displayName = String(entry?.displayName ?? '').trim();
    normalized.push({
      code,
      label: displayName || languageLabelFromCode(code),
    });
  }

  if (normalized.length < 1) {
    return voicesByLanguage ? toLanguageOptions(voicesByLanguage) : [];
  }

  return normalized;
};

export const normalizeGoogleCatalog = (raw: unknown, requestedLanguageCode = '') => {
  const voices = normalizeGoogleVoices(raw, requestedLanguageCode);
  const voicesByLanguage = groupVoicesByLanguage(voices);
  const languageOptions = normalizeGoogleLanguages(raw, voicesByLanguage);

  return { voicesByLanguage, languageOptions };
};

export const groupVoicesByLanguage = (voices: GoogleVoiceOption[]): Record<string, GoogleVoiceOption[]> => {
  const grouped: Record<string, GoogleVoiceOption[]> = {};

  for (const voice of voices) {
    if (!grouped[voice.languageCode]) {
      grouped[voice.languageCode] = [];
    }
    grouped[voice.languageCode].push(voice);
  }

  for (const languageCode of Object.keys(grouped)) {
    grouped[languageCode] = dedupeVoices(grouped[languageCode]).sort((a, b) => a.displayName.localeCompare(b.displayName));
    if (grouped[languageCode].length < 1) {
      delete grouped[languageCode];
    }
  }

  return grouped;
};

export const hasVoicesInCatalog = (voicesByLanguage: Record<string, GoogleVoiceOption[]>) => Object.values(voicesByLanguage).some((voices) => voices.length > 0);

const normalizeLanguageCode = (languageCode: string) => languageCode ? languageCode.trim().replace(/_/g, '-').toLowerCase() : '';

const findLanguageEntry = (voicesByLanguage: Record<string, GoogleVoiceOption[]>, languageCode: string): [string, GoogleVoiceOption[]] | null => {
  const normalizedRequested = normalizeLanguageCode(languageCode);
  if (!normalizedRequested) return null;

  const entries = Object.entries(voicesByLanguage).filter(([, voices]) => voices.length > 0);
  if (entries.length < 1) return null;

  const exactMatch = entries.find(([code]) => normalizeLanguageCode(code) === normalizedRequested);
  if (exactMatch) return exactMatch;

  const baseLanguage = normalizedRequested.split('-')[0];
  if (baseLanguage) {
    const baseMatch = entries.find(([code]) => normalizeLanguageCode(code).split('-')[0] === baseLanguage);
    if (baseMatch) return baseMatch;
  }

  return entries[0] ?? null;
};

export const resolveVoicesForLanguage = (voicesByLanguage: Record<string, GoogleVoiceOption[]>, languageCode: string): GoogleVoiceOption[] => {
  return findLanguageEntry(voicesByLanguage, languageCode)?.[1] ?? [];
};

const languageLabelFromCode = (code: string) => {
  if (LANGUAGE_LABEL_OVERRIDES[code]) {
    return LANGUAGE_LABEL_OVERRIDES[code];
  }

  const [language, region] = code.split('-');
  const base = languageDisplay?.of(language) ?? language ?? code;
  if (!region) return base;

  const regionLabel = regionDisplay?.of(region) ?? region;
  if (!regionLabel) return base;
  return `${base} (${regionLabel})`;
};

export const toLanguageOptions = (voicesByLanguage: Record<string, GoogleVoiceOption[]>): LanguageOption[] =>
  Object.keys(voicesByLanguage).map((code) => ({ code, label: languageLabelFromCode(code) }));

export const reconcileDraftWithCatalog = (draft: GoogleAiVoiceDraftV1, voicesByLanguage: Record<string, GoogleVoiceOption[]>): GoogleAiVoiceDraftV1 => {
  if (!hasVoicesInCatalog(voicesByLanguage)) {
    return draft;
  }

  const languages = Object.keys(voicesByLanguage)
    .filter((code) => (voicesByLanguage[code] ?? []).length > 0)
    .sort((a, b) => a.localeCompare(b));

  if (languages.length < 1) {
    return draft;
  }

  let selectedLanguage = draft.languageCode;

  if (!selectedLanguage) {
    selectedLanguage = languages[0];
  }

  const resolvedEntry = findLanguageEntry(voicesByLanguage, selectedLanguage);
  const languageVoices = resolvedEntry?.[1] ?? [];
  if (languageVoices.length < 1) {
    return { ...draft, languageCode: selectedLanguage };
  }

  const first = languageVoices[0];
  const second = languageVoices[Math.min(1, languageVoices.length - 1)] ?? first;
  const resolveSpeaker = (voiceId: string, fallback: GoogleVoiceOption) => languageVoices.find((voice) => voice.voiceId === voiceId) ?? fallback;

  const speakerA = resolveSpeaker(draft.speakers.speakerA.voiceId, first);
  const speakerB = resolveSpeaker(draft.speakers.speakerB.voiceId, second);

  if (
    draft.languageCode === selectedLanguage &&
    draft.speakers.speakerA.voiceId === speakerA.voiceId &&
    draft.speakers.speakerA.displayName === speakerA.displayName &&
    draft.speakers.speakerB.voiceId === speakerB.voiceId &&
    draft.speakers.speakerB.displayName === speakerB.displayName
  ) {
    return draft;
  }

  return {
    ...draft,
    languageCode: selectedLanguage,
    speakers: {
      speakerA: { voiceId: speakerA.voiceId, displayName: speakerA.displayName },
      speakerB: { voiceId: speakerB.voiceId, displayName: speakerB.displayName },
    },
  };
};
